import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchCampaignConfigSettings } from '../services/campaign';
import { saveCancellationConfigSettings } from '../services/cancellationRules';
import type { CancellationSettingRow } from '../services/cancellationRules';
import { campaignConfigSettingKeys } from './useCampaignConfigSettingMutations';

/**
 * Bespoke query + save mutation for the Cancellation Rules tab, mirroring
 * useCampaignCthfRules. The query key is the SHARED campaignConfigSettingKeys.settings
 * (campaignId) key (not a bespoke cancellation-only key) so it dedupes with any other
 * consumer of this campaign's settings and the save mutation's invalidation lands correctly.
 */
export const useCampaignCancellationRules = (campaignId?: string) => {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: campaignConfigSettingKeys.settings(campaignId ?? 'unresolved'),
        queryFn: () => {
            if (!campaignId) {
                throw new Error('useCampaignCancellationRules: campaignId is required to fetch cancellation rules');
            }
            return fetchCampaignConfigSettings(campaignId);
        },
        enabled: Boolean(campaignId),
    });

    const saveMutation = useMutation({
        mutationFn: (rows: CancellationSettingRow[]) => {
            if (!campaignId) {
                throw new Error('useCampaignCancellationRules: campaignId is required to save cancellation rules');
            }
            return saveCancellationConfigSettings(campaignId, rows);
        },
        onSuccess: () => {
            if (!campaignId) return;
            queryClient.invalidateQueries({ queryKey: campaignConfigSettingKeys.settings(campaignId) });
        },
    });

    return { query, saveMutation };
};
